import { alpha, Box, Chip, Stack, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { PaletteMode } from '@mui/material';
import React, { useMemo } from 'react';
import useThemeMode from 'src/Common/Providers/ThemeModeProvider';
import { createAppTheme } from 'src/Common/Theme/Default';
import { SettingsSection } from '../SettingsSection';

interface PreviewColors {
  window: string;
  header: string;
  headerText: string;
  brand: string;
  card: string;
  lineStrong: string;
  lineSoft: string;
  border: string;
}

interface ThemeOptionProps {
  label: string;
  selected: boolean;
  colors: PreviewColors;
  beta?: boolean;
  onSelect: () => void;
}

const ThemeOptionCard = ({ label, selected, colors, beta, onSelect }: ThemeOptionProps): React.JSX.Element => (
  <Stack
    role={'button'}
    onClick={onSelect}
    spacing={1.5}
    sx={{
      flex: 1,
      cursor: 'pointer',
      p: 1.5,
      borderRadius: 1,
      border: 2,
      borderColor: selected ? 'primary.main' : 'divider',
      transition: 'border-color 150ms, box-shadow 150ms',
      boxShadow: (theme) => (selected ? theme.shadows[3] : 'none'),
      '&:hover': { borderColor: selected ? 'primary.main' : 'text.disabled' },
    }}
  >
    {/* Mini app mock, painted from the real theme tokens. A positioned visual
        frame (overlays an absolute CheckCircle), not a flex container — Box. */}
    <Box
      sx={{
        position: 'relative',
        borderRadius: 1,
        overflow: 'hidden',
        border: `1px solid ${colors.border}`,
        bgcolor: colors.window,
      }}
    >
      {selected && <CheckCircle sx={{ position: 'absolute', top: 6, right: 6, color: 'primary.main', fontSize: 20 }} />}
      <Stack direction={'row'} alignItems={'center'} spacing={0.75} sx={{ height: 20, bgcolor: colors.header, px: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.brand }} />
        <Box sx={{ height: 4, width: '30%', borderRadius: 1, bgcolor: colors.headerText }} />
      </Stack>
      <Box sx={{ p: 1.25 }}>
        <Stack spacing={0.75} sx={{ borderRadius: 1, bgcolor: colors.card, p: 1 }}>
          <Box sx={{ height: 6, borderRadius: 1, bgcolor: colors.lineStrong, width: '70%' }} />
          <Box sx={{ height: 6, borderRadius: 1, bgcolor: colors.lineSoft, width: '45%' }} />
        </Stack>
      </Box>
    </Box>
    <Stack direction={'row'} spacing={1} alignItems={'center'} sx={{ px: 0.5 }}>
      <Typography variant={'subtitle2'} fontWeight={700}>
        {label}
      </Typography>
      {beta && <Chip label={'Beta'} size={'small'} color={'warning'} variant={'outlined'} />}
    </Stack>
  </Stack>
);

export const AppearanceSection = (): React.JSX.Element => {
  const { mode, setMode } = useThemeMode();

  // Build both themes from the same factory ThemeModeProvider uses, so each
  // preview shows that mode's real palette regardless of the active mode.
  const previews = useMemo<Record<PaletteMode, PreviewColors>>(() => {
    const toColors = (m: PaletteMode): PreviewColors => {
      const { palette } = createAppTheme(m);
      return {
        window: palette.background.default,
        header: palette.header.main,
        headerText: alpha(palette.header.contrastText, 0.55),
        brand: palette.primary.main,
        card: palette.background.paper,
        lineStrong: alpha(palette.text.primary, 0.2),
        lineSoft: alpha(palette.text.primary, 0.1),
        border: palette.divider,
      };
    };
    return { light: toColors('light'), dark: toColors('dark') };
  }, []);

  return (
    <SettingsSection title={'Appearance'} description={'Customize how the app looks on this device.'}>
      <Stack direction={'column'} spacing={2}>
        <Typography
          variant={'caption'}
          color={'text.secondary'}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Theme
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ maxWidth: 520 }}>
          <ThemeOptionCard
            label={'Light'}
            colors={previews.light}
            selected={mode === 'light'}
            onSelect={() => setMode('light')}
          />
          <ThemeOptionCard
            label={'Dark'}
            colors={previews.dark}
            beta
            selected={mode === 'dark'}
            onSelect={() => setMode('dark')}
          />
        </Stack>
        <Typography variant={'body2'} color={'text.secondary'}>
          Dark mode is still being polished — some screens may not look quite right yet. Your choice is saved on this
          device.
        </Typography>
      </Stack>
    </SettingsSection>
  );
};
